import React, { useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Breadcrumbs, Container, Link, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import SystemModal from '../navigation/modals/systemModal';
import { toast } from 'react-toastify';
import { sortApplicationItems } from '../common/utils/tableUtil';
import ClassificationBannerHeader from '../common/banner/classifactionBannerHeader';
import PageHeader from '../common/banner/pageHeader';
import SearchBar from '../common/search/SearchBar';
import { useNavigate } from 'react-router-dom';
import AlertsFilterButton from '../common/buttons/AlertsFilterButton';

const getFilteredSystems = (systems, filterValue) => {
  if (filterValue === 'all') return systems;

  return systems.filter(system => {
    if (filterValue === 'authorized') {
      return system.authorized === true;
    } else if (filterValue === 'rogue') {
      return system.authorized === false;
    }
    return true;
  });
}

const SystemTable = ({ items, onToggle, onIpClick, sortConfig, onSortChange, fetchSystems }) => {

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  }

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return null;
    
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUp fontSize="small" />
    ) : (
      <ArrowDropDown fontSize="small" />
    );
  }

  const systemUpdate = (system) => {
    toast.success(`System ${system.no} Updated`);
    fetchSystems();
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">    
          <TableHead>
            <TableRow sx={{ backgroundColor: '#0568AE' }}>
              {['IP (DNS)', 'Collector', 'Authorized', 'Notes'].map((field) => (
                <TableCell
                  key={field}
                  onClick={() => handleSort(field)}
                  sx={{ color: '#EEEEEE', fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                    <SortIcon field={field} />
                  </Stack>
                </TableCell>                
              ))}              
                <TableCell sx={{ color: '#EEEEEE', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={item.id} sx={{ height: 56, backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#000000' }}>

                <TableCell sx={{ color: '#FFFFFF' }}>
                    {item.ip_dns !== 'undefined' ? (
                      <Link underline="hover" color="#c1d4e7" 
                          sx={{ '&:hover': { cursor: 'pointer' } }}
                          onClick={ (e) => { onIpClick(e.target.text )}}
                      >
                          {item.ip_dns}
                      </Link>
                    ) : (
                      <span>undefined</span>
                    )}                
                </TableCell>
                
                <TableCell sx={{ color: '#FFFFFF' }}>{item.collector}</TableCell>
                <TableCell><Switch checked={!!item.authorized} onChange={(e) => onToggle(item, 'authorized', e.target.checked)} /></TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.notes}</TableCell>
                <TableCell>
                    <SystemModal system={item} onSystemUpdate={systemUpdate} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default function SystemSettings({ filter }) {
  const [systems, setSystems] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [systemsFilter, setSystemsFilter] = useState('all');

  const title = 'System Authorization';
  const { paramFilter } = useParams();
  const [ipv4, setIPv4] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (ipv4 != "") {
      navigate('/hostInformation', {state: {ipv4}})
    }
    fetchSystems();
    updateSystemsFilter();
  }, [ipv4]);

  const fetchSystems = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/systems/`);    
      if (!response.ok) {
          toast.error(`HTTP error! status: ${response.status}`);
      } else {
          const json = await response.json();
          setSystems(json.data);
      }
    } catch (e) {
      console.error(e);
    }
  }

  const updateSystemsFilter = () => {
    if (paramFilter !== undefined) {
      setSystemsFilter(paramFilter);
    }
  }

  const handleUpdate = async (item) => {
      try {
          const res = await fetch(`${process.env.REACT_APP_API}/systems/${item.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(item)
          });
          if (res.ok) {
              setSystems((prev) => prev.map((p) => (p.id === item.id ? item : p)));
              if (item.authorized) {
                  toast.success(`System ${item.no} Authorized`);
              } else {
                toast.success(`System ${item.no} De-Authorized`);
              }
              
          } else {
              toast.error(`Failed To Update System ${item.no} : ${res.status}`);
          }
      } catch (err) {
          toast.error("Error Updating System Status : ", err);
        }
  }

  const handleIpClick = (ip) => {
      setIPv4(ip);
  };

  const handleSortChange = (newConfig) => {
    setSortConfig(newConfig);
  };

  const handleFilterChange = (value) => {
    setSystemsFilter(value);
    if (value === 'all') {
      navigate(`/systems`);
    } else {
      navigate(`/systems/${value}`);
    }
  };

    // apply alerts filter
  const systemsFiltered = getFilteredSystems(systems, paramFilter);

  const filtered = systemsFiltered.filter((item) => {
    const s = searchQuery.toLowerCase();
    return ['collector', 'notes'].some((field) => item[field]?.toLowerCase().includes(s));
  });

  const sorted = sortApplicationItems(filtered, sortConfig);
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleBreadcrumbClick = () => {
    navigate('/alerts');
  }

  return (
    <Container sx={{ minWidth:1800, mt: 10, mb: 10 }}>
      <Box sx={{pt: 2}}>
        <ClassificationBannerHeader />
        <PageHeader title="Systems"></PageHeader>
      </Box>
      <Box>
        <Box sx={{ display: 'flex', mt: 2, justifyContent: 'center', gap: '32px', width: '100%', maxwidth: '1600px', margin: '30px auto 0' }}>
            <Stack  sx={{ width: '47%', paddingLeft: 0, marginLeft: 0}} spacing={{ md: 4 }}  >
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="#c1d4e7" onClick={handleBreadcrumbClick} sx={{ '&:hover': { cursor: 'pointer' } }}>
                        Alerts
                    </Link>
                    <Typography color="white"> / </Typography>
                    <Typography color="white">{ title }</Typography>
                </Breadcrumbs>
            </Stack>
            <Stack direction="row" justifyContent="flex-end" sx={{ width: '50%', mx: 'auto' }}>          
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search systems..."
                label="Filter"
                width={200}
              />
              <AlertsFilterButton
                value={systemsFilter}
                onChange={(value) => {
                  handleFilterChange(value);
                  setPage(0);
                }}
              />
            </Stack>
        </Box>

        <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: '#1A1A1A'  }}>
          <SystemTable
              items={paginated}
              onToggle={(item, key, value) => handleUpdate({ ...item, [key]: value })}
              onIpClick={(ip) => handleIpClick(ip) }
              sortConfig={sortConfig}
              onSortChange={handleSortChange}
              fetchSystems={fetchSystems}
          />
          <TablePagination
              component="div"
              count={sorted.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
          />
        </TableContainer>
      </Box>
    </Container>
  );
}